import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent,
  DialogTitle, DialogContent, DialogActions, Chip, IconButton, Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { apiUrl, handleApiError, downloadBlob } from './utils/api';
import { formatDate } from './utils/date';
import NoMatchingRecords from './components/NoMatchingRecords';
import { useNotify } from './components/NotificationProvider';
import PdfPreviewDialog from './components/PdfPreviewDialog';
import AppDialog from './components/AppDialog';

const base = `${apiUrl}/api/field-tickets`;
const money = (v) => (v == null ? '$0.00' : `$${Number(v).toFixed(2)}`);
const num = (v) => (v == null || v === '' ? '' : Number(v).toFixed(2));

// Group invoice lines by date (they arrive ordered by date), summing loads + qty per day — this
// drives the per-date subtotal rows that match the Ivy's → Williams Brothers invoice layout.
const groupByDate = (lines) => {
  const groups = [];
  lines.forEach((l) => {
    const key = l.date || '';
    let g = groups.length && groups[groups.length - 1].date === key ? groups[groups.length - 1] : null;
    if (!g) { g = { date: key, rows: [], loads: 0, qty: 0 }; groups.push(g); }
    g.rows.push(l);
    g.loads += Number(l.loads || 0);
    g.qty += Number(l.quantity || 0);
  });
  return groups;
};

export default function FieldTicketInvoice() {
  const todayIso = new Date().toISOString().split('T')[0];
  const [customers, setCustomers] = useState([]);
  const [billedTo, setBilledTo] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(todayIso);
  const [invoiceFor, setInvoiceFor] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [basis, setBasis] = useState('LOAD');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const notify = useNotify();

  // "Merge customer names" — reconcile OCR spelling drift ("W Brittany" -> "Williams Brothers").
  const [mergeOpen, setMergeOpen] = useState(false);
  const [variants, setVariants] = useState([]);   // [{ raw, canonical, count, mapped }]
  const [mergeBusy, setMergeBusy] = useState(false);

  // Scan preview: the ticket image behind an invoice line (also appended to the PDF packet).
  const [preview, setPreview] = useState(null);   // { src, label } | null
  // Full invoice preview: the generated PDF (invoice sheet + attached scans) shown inline before print.
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadCustomers = () => {
    axios.get(`${base}/customers`)
      .then((res) => setCustomers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCustomers([]));
  };

  useEffect(() => { loadCustomers(); }, []);

  const openMerge = async () => {
    try {
      const res = await axios.get(`${base}/customer-variants`);
      // Local `to` field per row = what it maps to (prefilled with current canonical).
      setVariants((Array.isArray(res.data) ? res.data : []).map((v) => ({ ...v, to: v.canonical })));
      setMergeOpen(true);
    } catch (err) {
      notify.error(handleApiError(err, 'Failed to load customer names'));
    }
  };

  const saveMerge = async (row) => {
    const to = (row.to || '').trim();
    if (!to) { notify.warning('Enter the name to merge into.'); return; }
    if (to === row.raw) { notify.info('That name is already itself — nothing to merge.'); return; }
    try {
      setMergeBusy(true);
      await axios.post(`${base}/customer-aliases`, { sourceName: row.raw, canonicalName: to });
      notify.success(`"${row.raw}" now bills as "${to}".`);
      const res = await axios.get(`${base}/customer-variants`);
      setVariants((Array.isArray(res.data) ? res.data : []).map((v) => ({ ...v, to: v.canonical })));
      loadCustomers();
    } catch (err) {
      notify.error(handleApiError(err, 'Failed to save merge'));
    } finally {
      setMergeBusy(false);
    }
  };

  // Existing canonical names to suggest as merge targets (the ones already mapped-to or standalone).
  const canonicalSuggestions = Array.from(new Set(variants.map((v) => v.canonical))).sort();

  const params = () => ({
    billedTo, startDate, endDate, invoiceNumber, invoiceDate, invoiceFor, customerEmail, basis,
  });

  const generate = async () => {
    if (!billedTo || !startDate || !endDate) {
      notify.warning('Pick a customer and a date range.');
      return;
    }
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const res = await axios.get(`${base}/invoice`, { params: params() });
      setInvoice(res.data);
      if (res.data?.unpricedCombos?.length) {
        notify.warning(`No rate for: ${res.data.unpricedCombos.join(', ')} — priced at $0. Add it on Rate Cards.`);
      }
    } catch (err) {
      setError(handleApiError(err, 'Failed to generate invoice'));
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${base}/invoice/pdf`, { params: params(), responseType: 'blob' });
      downloadBlob(res, `${invoiceNumber || 'field-ticket-invoice'}.pdf`);
    } catch (err) {
      notify.error(handleApiError(err, 'PDF failed'));
    }
  };

  // Preview the whole invoice (sheet + attached ticket scans) inline before printing/downloading.
  const openPreview = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${base}/invoice/pdf`, { params: params(), responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(res.data));
    } catch (err) {
      notify.error(handleApiError(err, 'Preview failed'));
    } finally {
      setPdfBusy(false);
    }
  };
  const closePreview = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  const clearFilters = () => {
    setBilledTo('');
    setStartDate('');
    setEndDate('');
    setInvoiceNumber('');
    setInvoiceDate(todayIso);
    setInvoiceFor('');
    setCustomerEmail('');
    setBasis('LOAD');
    setInvoice(null);
    setSearched(false);
    setError('');
  };

  const lines = invoice?.lines || [];
  const hourly = basis === 'HOUR';
  const groups = groupByDate(lines);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Field Ticket Invoice</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Bills verified Ivy field tickets to a customer (e.g. Williams Brothers) for a period, priced by truck
        type and basis from the rate card. Hourly and per-load are separate invoices — pick the Basis (hourly
        adds a Loads column). The PDF renders the Ivy's layout with per-day subtotals and the Goodman Factors
        remittance block.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField label="Customer (Billed To)" select value={billedTo} onChange={(e) => setBilledTo(e.target.value)} sx={{ minWidth: 200 }} size="small">
            {customers.length === 0 && <MenuItem value="" disabled><em>No customers yet</em></MenuItem>}
            {customers.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <Button size="small" startIcon={<MergeTypeIcon />} onClick={openMerge} sx={{ textTransform: 'none' }}>
            Merge names
          </Button>
          <TextField label="Basis" select value={basis} onChange={(e) => setBasis(e.target.value)} sx={{ minWidth: 130 }} size="small">
            <MenuItem value="LOAD">Per load</MenuItem>
            <MenuItem value="HOUR">Hourly</MenuItem>
          </TextField>
          <TextField label="Period start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Period end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Invoice for (job)" value={invoiceFor} onChange={(e) => setInvoiceFor(e.target.value)} size="small" sx={{ minWidth: 220 }}
            placeholder="e.g. PIT #4 PER LOAD" />
          <TextField label="Invoice #" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} size="small" placeholder="WB-2026-0182" />
          <TextField label="Invoice date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
          <TextField label="Customer AP email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} size="small" sx={{ minWidth: 220 }} />
          <Button variant="contained" onClick={generate} disabled={loading}>Generate</Button>
          <Button variant="outlined" onClick={clearFilters} disabled={loading}>Clear</Button>
          {invoice && lines.length > 0 && (
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={openPreview} disabled={pdfBusy}>
              {pdfBusy ? 'Loading…' : 'Preview'}
            </Button>
          )}
          {invoice && lines.length > 0 && (
            <Tooltip title="Invoice sheet + every ticket's scan, one per page">
              <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>PDF + scans</Button>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}

      {invoice && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
            <Card variant="outlined"><CardContent>
              <Typography variant="overline">Tickets</Typography>
              <Typography variant="h6">{invoice.ticketCount}</Typography>
            </CardContent></Card>
            <Card variant="outlined"><CardContent>
              <Typography variant="overline">Total Due</Typography>
              <Typography variant="h6">{money(invoice.total)}</Typography>
            </CardContent></Card>
          </Stack>

          {invoice.unpricedCombos?.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No rate found for: {invoice.unpricedCombos.join(', ')} — those lines are $0. Add the rate on
              the Rate Cards screen (code LOAD or HOUR, matching truck type).
            </Alert>
          )}
        </>
      )}

      {searched && !loading && lines.length === 0 && <NoMatchingRecords />}

      {lines.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#EAEAEA' }}>
                <TableCell>Date</TableCell>
                <TableCell>Truck #</TableCell>
                {hourly && <TableCell align="right">Loads</TableCell>}
                <TableCell>Ticket #</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <React.Fragment key={g.date || 'nodate'}>
                  {g.rows.map((l, i) => (
                    <TableRow key={l.ticketId} hover>
                      <TableCell>{i === 0 ? formatDate(g.date) : ''}</TableCell>
                      <TableCell>{l.truckNo}</TableCell>
                      {hourly && <TableCell align="right">{num(l.loads)}</TableCell>}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>{l.ticketNo}</span>
                          <Tooltip title="View scanned ticket">
                            <IconButton size="small" aria-label="View scanned ticket"
                              onClick={() => setPreview({ src: `${base}/${l.ticketId}/image?t=${Date.now()}`, label: `Ticket #${l.ticketNo} · Truck ${l.truckNo} · ${formatDate(l.date)}` })}>
                              <ImageSearchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">{num(l.quantity)}</TableCell>
                      <TableCell align="right">{money(l.rate)}</TableCell>
                      <TableCell align="right">{money(l.amount)}</TableCell>
                    </TableRow>
                  ))}
                  {/* per-date subtotal */}
                  <TableRow sx={{ bgcolor: '#F0F0F0' }}>
                    <TableCell />
                    <TableCell />
                    {hourly && <TableCell align="right"><b>{g.loads.toFixed(2)}</b></TableCell>}
                    <TableCell />
                    <TableCell align="right"><b>{g.qty.toFixed(2)}</b></TableCell>
                    <TableCell />
                    <TableCell align="right">$ -</TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {/* grand total */}
              <TableRow sx={{ bgcolor: '#E0E0E0' }}>
                <TableCell colSpan={2} />
                {hourly && <TableCell align="right"><b>{Number(invoice.totalLoads || 0).toFixed(2)}</b></TableCell>}
                <TableCell />
                <TableCell align="right"><b>{Number(invoice.totalQuantity || 0).toFixed(2)}</b></TableCell>
                <TableCell />
                <TableCell align="right"><b>{money(invoice.total)}</b></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Full invoice preview (invoice sheet + attached scans) in an iframe, with Print / Download. */}
      <PdfPreviewDialog
        open={!!pdfUrl}
        onClose={closePreview}
        url={pdfUrl}
        title={`Invoice preview${billedTo ? ` — ${billedTo}` : ''}`}
        fileName={`${invoiceNumber || 'field-ticket-invoice'}.pdf`}
      />

      {/* Scan preview: the ticket image behind a line (same scans get appended to the PDF packet). */}
      <AppDialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="lg"
        PaperProps={{ sx: { height: { xs: 'auto', md: '90vh' } } }}>
        <DialogTitle sx={{ pr: 6 }}>{preview?.label || 'Scanned ticket'}</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
          {preview && (
            <a href={preview.src} target="_blank" rel="noreferrer" title="Open full-size in a new tab" style={{ display: 'block', width: '100%' }}>
              <img src={preview.src} alt="scanned ticket"
                style={{ width: '100%', height: 'auto', maxHeight: '82vh', objectFit: 'contain',
                  border: '1px solid #ddd', borderRadius: 4, cursor: 'zoom-in' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.insertAdjacentHTML('afterend', '<p style="color:#6A7682">No scan stored for this ticket.</p>'); }}
              />
            </a>
          )}
        </DialogContent>
      </AppDialog>

      {/* Merge customer names: reconcile OCR spelling drift so a week's loads roll into one invoice. */}
      <AppDialog open={mergeOpen} onClose={() => setMergeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Merge customer names</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Handwritten tickets spell a customer several ways ("W Brittany" vs "Williams Brothers").
            Map each variant to one name so its loads bill together. This applies to tickets already
            scanned — no re-scan needed.
          </Typography>
          {variants.length === 0 && (
            <Typography variant="body2" color="text.secondary">No customer names on file yet.</Typography>
          )}
          {variants.map((v, i) => (
            <Stack key={v.raw} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ minWidth: 150 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{v.raw}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {v.count} ticket{v.count === 1 ? '' : 's'}
                  {v.mapped && <Chip size="small" label="mapped" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                </Typography>
              </Box>
              <Typography color="text.secondary">→</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="bills as…"
                value={v.to}
                onChange={(e) => setVariants((prev) => prev.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
                inputProps={{ list: 'ftc-canonicals' }}
              />
              <Button size="small" variant="outlined" disabled={mergeBusy} onClick={() => saveMerge(v)}>
                Save
              </Button>
            </Stack>
          ))}
          <datalist id="ftc-canonicals">
            {canonicalSuggestions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeOpen(false); loadCustomers(); }}>Done</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
